import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import CloudflareTurnstile from 'react-turnstile';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { ITurnstileVerifyPayload, TurnstileType } from './types';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { AnimatePresence, motion } from 'framer-motion';

interface Props {
  onVerify: (payload: ITurnstileVerifyPayload) => void;
  language: string;
  className?: string;
}

export const TURNSTILE_TIMEOUT_MS = 5000;
export const PROGRESS_INTERVAL_MS = 100;

const Turnstile = ({ onVerify, language, className }: Props): ReactElement => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [progress, setProgress] = useState(0);

  const timeoutRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const stopVerifying = () => {
    setIsVerifying(false);
    clearTimers();
  };

  const startVerification = () => {
    setIsVerifying(true);
    setProgress(0);

    const startTime = Date.now();

    intervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const value = Math.min((elapsed / TURNSTILE_TIMEOUT_MS) * 100, 100);
      setProgress(value);
    }, PROGRESS_INTERVAL_MS);

    timeoutRef.current = window.setTimeout(() => {
      if (!isInteractive) {
        setUseFallback(true);
      }
    }, TURNSTILE_TIMEOUT_MS);
  };

  useEffect(() => {
    if (useFallback) {
      stopVerifying();
    }
  }, [useFallback]);

  const progressBar = useMemo(
    () => (
      <AnimatePresence>
        {isVerifying && !isInteractive && (
          <motion.div
            key="turnstile-progress"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'linear' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    borderRadius: '9999px',
                    height: 4,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '9999px',
                    },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <p className="text-xs">{`${Math.round(progress)}%`}</p>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    ),
    [isVerifying, isInteractive, progress],
  );

  if (useFallback) {
    return (
      <div className={className}>
        <HCaptcha
          sitekey={import.meta.env.VITE_HCAPTCHA_SITEKEY}
          languageOverride={language}
          onVerify={(token) => onVerify({ token, type: TurnstileType.HCaptcha })}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {progressBar}
      <CloudflareTurnstile
        sitekey={import.meta.env.VITE_TURNSTILE_SITEKEY}
        language={language}
        theme="light"
        onLoad={startVerification}
        onBeforeInteractive={() => {
          setIsInteractive(true);
          stopVerifying();
        }}
        onVerify={(token) => {
          stopVerifying();
          onVerify({ token, type: TurnstileType.Cloudflare });
        }}
        onError={() => {
          stopVerifying();
          setUseFallback(true);
        }}
        onExpire={() => {
          stopVerifying();
          setUseFallback(true);
        }}
        onUnsupported={() => {
          stopVerifying();
          setUseFallback(true);
        }}
      />
    </div>
  );
};

export default Turnstile;
